import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { supabase } from '../utils/supabaseClient';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../context/AuthContext';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import TextField from '@mui/material/TextField';

// Helper function for consistent date handling
const getAdjustedDate = (dateInput) => {
  // Handle if input is already a Date object
  if (dateInput instanceof Date) {
    const date = new Date(dateInput);
    date.setUTCHours(12, 0, 0, 0);
    return date;
  }
  
  // Handle string input
  if (typeof dateInput === 'string') {
    // If it's already an ISO string with time, use it as is
    if (dateInput.includes('T')) {
      return new Date(dateInput);
    }
    // Otherwise append noon UTC time to ensure date consistency
    return new Date(`${dateInput}T12:00:00Z`);
  }
  
  // Return null for undefined or null input
  return null;
};

function UploadRefusal() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadId, setUploadId] = useState(null);
  const [workWeekDialogOpen, setWorkWeekDialogOpen] = useState(false);
  const [workWeekFromDate, setWorkWeekFromDate] = useState(null);
  const [workWeekToDate, setWorkWeekToDate] = useState(null);
  const [customFileName, setCustomFileName] = useState('');
  const [dateError, setDateError] = useState('');
  const { user } = useAuth();
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
        setError('');
      }
    }
  });
  
  async function handleUpload() {
    if (!file) {
      setError('Please select a file first');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 1. Upload file to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `uploads/refusal/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw uploadError;
      }
      
      // 2. Create a record in the uploads table with user info
      const { data: uploadData, error } = await supabase
        .from('uploads')
        .insert({
          filename: file.name,
          original_filename: file.name,
          file_path: filePath,
          file_size: file.size,
          status: 'pending',
          user_id: user?.id,
          uploaded_by: user?.email
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Store the upload ID for potential work week addition
      if (uploadData) {
        setUploadId(uploadData.id);
        // Show the work week dialog
        setWorkWeekDialogOpen(true);
      } else {
        // Navigate to calculation page if no data returned
        navigate(`/calculate/${uploadData.id}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.message || 'An error occurred while uploading the file');
      setLoading(false);
    }
  }

  const handleWorkWeekSave = async () => {
    // Validate dates
    if (!workWeekFromDate || !workWeekToDate) {
      setDateError('Both From and To Dates are required.');
      return;
    }

    // Check that To Date is not earlier than From Date
    if (workWeekToDate < workWeekFromDate) {
      setDateError('To Date cannot be earlier than From Date.');
      return;
    }

    setDateError('');

    try {
      // Create adjusted dates at 12:00 UTC to prevent timezone shifting issues
      const adjustedFromDate = getAdjustedDate(workWeekFromDate);
      const adjustedToDate = getAdjustedDate(workWeekToDate);
      
      // Update the upload record with work week dates and custom file name
      const { error } = await supabase
        .from('uploads')
        .update({
          work_week_from_date: adjustedFromDate.toISOString(),
          work_week_to_date: adjustedToDate.toISOString(),
          filename: customFileName || file.name
        })
        .eq('id', uploadId);

      if (error) {
        throw error;
      }

      // Close dialog and navigate to calculation
      setWorkWeekDialogOpen(false);
      navigate(`/calculate/${uploadId}`);
    } catch (error) {
      console.error('Error saving work week:', error);
      setDateError(`Error saving work week: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkWeekCancel = () => {
    setWorkWeekDialogOpen(false);
    setWorkWeekFromDate(null);
    setWorkWeekToDate(null);
    setCustomFileName('');
    setDateError('');
    // Still navigate to calculation page if they cancel
    navigate(`/calculate/${uploadId}`);
  };
  
  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Upload Refusal Work
        </Typography>
        <Typography paragraph>
          Upload a CSV file with refusal orders to calculate contractor payments based on refusal rates.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box 
          {...getRootProps()} 
          sx={{ 
            border: '2px dashed #cccccc',
            borderRadius: 2,
            p: 5,
            mb: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.05)'
            }
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
          
          {file ? (
            <Typography variant="body1">
              Selected file: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
            </Typography>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                {isDragActive ? 'Drop the file here...' : 'Drag and drop a CSV file here, or click to select'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Supported formats: .csv
              </Typography>
            </>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleUpload}
            disabled={!file || loading}
            sx={{ minWidth: 200 }}
          >
            {loading ? 'Uploading...' : 'Upload File'}
          </Button>
        </Box>
      </Paper>

      {/* Work Week Dialog */}
      <Dialog
        open={workWeekDialogOpen}
        onClose={handleWorkWeekCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Optional Upload Information</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add optional information for this upload.
          </DialogContentText>
          
          {dateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dateError}
            </Alert>
          )}
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              mt: 1, 
              mb: 1,
              borderBottom: '1px solid #e0e0e0',
              pb: 1
            }}
          >
            File Information
          </Typography>
          
          <TextField
            label="Custom File Name"
            value={customFileName}
            onChange={(e) => setCustomFileName(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Enter a custom file name (optional)"
          />
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              mt: 3, 
              mb: 1,
              borderBottom: '1px solid #e0e0e0',
              pb: 1
            }}
          >
            Work Week Date Range
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={workWeekFromDate}
                onChange={(newValue) => setWorkWeekFromDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
              
              <DatePicker
                label="To Date"
                value={workWeekToDate}
                onChange={(newValue) => setWorkWeekToDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWorkWeekCancel}>Skip</Button>
          <Button 
            onClick={handleWorkWeekSave} 
            color="primary" 
            variant="contained"
            disabled={!workWeekFromDate || !workWeekToDate}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UploadRefusal; 